import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { cloneDeep, find, forEach, get, isEmpty, isEqual, isNil, map } from 'lodash';
import turf from '@turf/turf';
import uuid from 'uuid/v1';

import * as mapOps from '../../../api/Maps/map-operations';

import LayerControl from '../../atoms/LayerControl';
import ButtonOnMap from '../ButtonOnMap';
import CesiumViewer from '../../components/CesiumViewer';

const { defaultMapCenter } = Meteor.settings.public;

let C;
let drawViewerLayers;
const oldLayers = [];
const oldMap = [];

const DEFAULT_THUMBNAIL_IMAGE = '/map-gallery-default.png';

class CesiumViewerContainerViewMap extends Component {
  baseMapsToDisplay = null;

  cesiumBaseLayerPicker = React.createRef();

  layerControl = React.createRef();

  layersToDisplay = null;

  randomMapId = uuid();

  mapViewer = null;

  mapViewerComponent = React.createRef();

  state = {
    clientModulesReady: false,
    isMapViewerLoaded: false,
    isCesiumViewerLoaded: get(this, 'mapViewerComponent.current.state.isMapLoaded', false),
    showLayerControl: false
  };

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) this.loadClientModules();
  }

  componentDidUpdate = async prevProps => {
    const { baseMaps, layers, map: mapObject, updateLayerMapState } = this.props;
    const { baseMaps: prevBaseMaps, layers: prevLayers } = prevProps;
    const { clientModulesReady, isMapViewerLoaded, isCesiumViewerLoaded } = this.state;
    let forceupdate = false;
    let forceupdateMap = false;

    if (clientModulesReady && isCesiumViewerLoaded && !isMapViewerLoaded) this.initializeViewer();

    if (!isEqual(layers, oldLayers[0])) {
      forceupdate = true;
      oldLayers[0] = cloneDeep(layers);
    } else {
      forceupdate = false;
    }

    if (!isEqual(mapObject, oldMap[0])) {
      forceupdateMap = true;
      oldMap[0] = cloneDeep(mapObject);
    } else {
      forceupdateMap = false;
    }

    if (isMapViewerLoaded) {
      if (
        !isEqual(baseMaps, prevBaseMaps) ||
        !isEqual(layers, prevLayers) ||
        forceupdateMap === true ||
        forceupdate === true
      ) {
        const cesiumObjectResults = await drawViewerLayers({
          baseMaps,
          baseMapsToDisplay: this.baseMapsToDisplay,
          baseMapsTransparency: mapOps.getBaseMapsTransparency(mapObject),
          baseMapsZoom: mapOps.getBaseMapsZoom(mapObject),
          cesiumViewer: this.mapViewer,
          isUpdate: true,
          layers,
          layersOverlayed: mapOps.areLayersOverlayed(mapObject),
          layersToDisplay: this.layersToDisplay,
          layersTransparency: mapOps.getLayersTransparency(mapObject),
          layersZoom: mapOps.getLayersZoom(mapObject)
        });
        this.layersToDisplay = cesiumObjectResults.newLayersToDraw;
        this.baseMapsToDisplay = cesiumObjectResults.newBaseMapsToDraw;
        updateLayerMapState(false);
      }
      if (!isEqual(baseMaps, prevBaseMaps)) {
        this.cesiumBaseLayerPicker.viewModel.imageryProviderViewModels = map(
          this.baseMapsToDisplay,
          ({ imageryProvider, ipsilumId: _id }, key) =>
            new C.ProviderViewModel({
              name: key,
              tooltip: key,
              iconUrl: get(find(baseMaps, ['_id', _id]), 'imageUrl') || DEFAULT_THUMBNAIL_IMAGE,
              creationFunction: () => imageryProvider
            })
        );
      }
    }
  };

  changeShowLayerControl = () =>
    this.setState(prevState => ({ showLayerControl: !prevState.showLayerControl }));

  initializeViewer = async () => {
    const {
      baseMaps,
      layers,
      map: mapObject,
      updateLayerMapState,
      userControlSetting
    } = this.props;

    C.Ion.defaultAccessToken =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI2ZGJjMTRlMi05OGEzLTRmN2EtYWM1YS1lZWEwNzk1MTJkZWEiLCJpZCI6MzI4NjEsInNjb3BlcyI6WyJhc3IiLCJnYyJdLCJpYXQiOjE1OTc2Nzk1MTJ9.klLApuKzrQ8VDAYhLpcYGrfL09cfxRh6dghlR2GmC-E';

    let mapLocation = mapOps.getMapLocation(mapObject);
    if (isEmpty(mapLocation)) {
      if (!isNil(Meteor.user())) mapLocation = userControlSetting.defaultMapCenter;
      else mapLocation = defaultMapCenter;
    }
    const homeCameraView = {
      destination: C.Cartesian3.fromDegrees(...turf.getCoords(mapLocation), 4000),
      orientation: { heading: C.Math.toRadians(0), pitch: C.Math.toRadians(-45) },
      duration: 0.25
    };

    this.mapViewer = get(this, 'mapViewerComponent.current.cesiumViewer');
    this.mapViewer.camera.flyTo(homeCameraView);
    this.mapViewer.homeButton.viewModel.command.beforeExecute.addEventListener(event => {
      // eslint-disable-next-line no-param-reassign
      event.cancel = true;
      this.mapViewer.camera.flyTo(homeCameraView);
    });
    const cesiumObjectResults = await drawViewerLayers({
      baseMaps,
      baseMapsToDisplay: this.baseMapsToDisplay,
      baseMapsTransparency: mapOps.getBaseMapsTransparency(mapObject),
      baseMapsZoom: mapOps.getBaseMapsZoom(mapObject),
      cesiumViewer: this.mapViewer,
      layers,
      layersOverlayed: mapOps.areLayersOverlayed(mapObject),
      layersToDisplay: this.layersToDisplay,
      layersTransparency: mapOps.getLayersTransparency(mapObject),
      layersZoom: mapOps.getLayersZoom(mapObject)
    });
    this.layersToDisplay = cesiumObjectResults.newLayersToDraw;
    this.baseMapsToDisplay = cesiumObjectResults.newBaseMapsToDraw;

    const cesiumViewerToolbar = get(this.mapViewer, '_toolbar');
    this.cesiumBaseLayerPicker = new C.BaseLayerPicker(cesiumViewerToolbar, {
      globe: get(this.mapViewer, 'cesiumWidget.scene.globe'),
      imageryProviderViewModels: map(
        this.baseMapsToDisplay,
        ({ imageryProvider, ipsilumId: _id }, key) =>
          new C.ProviderViewModel({
            name: key,
            tooltip: key,
            iconUrl: get(find(baseMaps, ['_id', _id]), 'imageUrl') || DEFAULT_THUMBNAIL_IMAGE,
            creationFunction: () => imageryProvider
          })
      )
    });

    this.setState({ isMapViewerLoaded: true });
    updateLayerMapState(false);
  };

  selectLayer = event => {
    const { map: mapObject } = this.props;
    const { checked, id } = event.target;

    const dataSources = get(this.mapViewer, 'dataSources._dataSources');
    const selectedLayer = find(dataSources, ['ipsilumId', id]);
    if (!mapOps.areLayersOverlayed(mapObject)) {
      forEach(dataSources, dataSource => {
        // eslint-disable-next-line no-param-reassign
        dataSource.show = false;
      });
    }
    if (!isNil(selectedLayer))
      selectedLayer.show = !mapOps.areLayersOverlayed(mapObject) || checked;
  };

  updateViewerState = newState => this.setState({ isCesiumViewerLoaded: newState });

  async loadClientModules() {
    if (Meteor.isClient) {
      C = await import('cesium');
      ({ drawViewerLayers } = await import('../../../modules/cesium-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { isAtlas, isFullScreen, layers, map: mapObject } = this.props;
    const { isMapViewerLoaded, showLayerControl } = this.state;

    const mapId = isAtlas ? this.randomMapId : 'mapViewer';

    return (
      <>
        <CesiumViewer
          isAtlas={isAtlas}
          isFullScreen={isFullScreen}
          mapId={mapId}
          ref={this.mapViewerComponent}
          updateViewerState={this.updateViewerState}
          {...this.props}
        />
        {isMapViewerLoaded && (
          <ButtonOnMap numberOfButtonsTop={0.75}>
            <div
              ref={this.layerControl}
              title="Layers"
              role="button"
              onKeyPress={() => ''}
              tabIndex={0}
              style={{ zIndex: 2 }}
              onClick={this.changeShowLayerControl}
            >
              <img
                src="/maps-buttons/capas.svg"
                style={{ height: '34px', width: '34px', cursor: 'pointer' }}
                alt="Layers"
              />
              <LayerControl
                layers={layers}
                layersOverlayed={mapOps.areLayersOverlayed(mapObject)}
                show={showLayerControl}
                title="Layers"
                selectLayer={this.selectLayer}
              />
            </div>
          </ButtonOnMap>
        )}
      </>
    );
  }
}

CesiumViewerContainerViewMap.defaultProps = {
  isAtlas: false,
  isFullScreen: false,
  updateLayerMapState: () => console.info('Public Map'),
  userControlSetting: {}
};

CesiumViewerContainerViewMap.propTypes = {
  baseMaps: PropTypes.array.isRequired,
  isAtlas: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  layers: PropTypes.array.isRequired,
  map: PropTypes.object.isRequired,
  updateLayerMapState: PropTypes.func,
  userControlSetting: PropTypes.object
};

export default CesiumViewerContainerViewMap;
